import { module } from 'modujs';
import { Swiper, Navigation, Controller, Pagination, Lazy } from 'swiper';
import { gsap } from 'gsap';
import { lerp } from '../utils/maths'

export default class extends module {
    constructor(m) {
        super(m);
    }

    init() {
        this.slides = Array.from(this.$('item')).map((item) => {
            return {
                ids: item.getAttribute('data-id').split(','),
                el: item.outerHTML
            }
        })

        this.paginationSlides = Array.from(this.$('item-pagination')).map((item) => {
            return {
                ids: item.getAttribute('data-id').split(','),
                el: item
            }
        })

        this.controlsHasInit = false

        this.currentSlides = []

        Swiper.use([Navigation, Controller, Pagination, Lazy]);

        this.container = this.$('container')[0];
        this.arrowNext = this.$('next')[0]
        this.arrowPrev = this.$('prev')[0]
        this.pagination = this.$('pagination')[0]

        this.carousel = new Swiper(this.container, {
            speed: 900,
            loop: (this.slides.length > 1) ? true : false,
            spaceBetween: 0,
            grabCursor: (this.slides.length > 1) ? true : false,
            simulateTouch: (this.slides.length > 1) ? true : false,
            slidesPerView: 1,
            threshold: 2,
            preloadImages: false,
            preventInteractionOnTransition: false,
            lazy: {
                loadPrevNext: true,
            },
            navigation: {
                prevEl: this.arrowPrev,
                nextEl: this.arrowNext
            },
            pagination: (this.slides.length > 1) ? { el: this.pagination, clickable: true } : false
        });

        this.controls = this.$('controls');

        if(this.controls.length && this.slides.length > 1) {
            this.initControls()
        } else {
            this.updateControls(0)
        }

        this.updatePaginationImages(null)
    }

    /**
     * Filter slides by color
     *
     * @param
     */
    filter(id) {
        // clear current slides
        this.currentSlides = []

        if(id == null) {
            this.slides.forEach((item) => {
                this.currentSlides.push(item.el)
            })
        } else {
            this.slides.forEach((item) => {
                if(item.ids.includes(id)) {
                    this.currentSlides.push(item.el)
                }
            })
        }

        //if no images found
        if(this.currentSlides.length == 0) {
            this.slides.forEach((item) => {
                this.currentSlides.push(item.el)
            })
        }

        this.carousel.removeAllSlides();
        this.carousel.params.loopedSlides = this.currentSlides.length
        this.carousel.params.loop = (this.currentSlides.length > 1) ? true : false
        this.carousel.params.grabCursor = (this.currentSlides.length > 1) ? true : false
        this.carousel.params.simulateTouch = (this.currentSlides.length > 1) ? true : false,
        this.carousel.appendSlide(this.currentSlides)
        this.carousel.lazy.load()
        this.carousel.slideTo(0,0)

        this.updatePaginationImages(id)
        this.updateControls(this.currentSlides.length)
    }

    /**
     * Update controls
     *
     * @param
     */
    updateControls(length) {
        if(length > 1) {
            this.el.classList.remove('-hide-controls')
        } else {
            this.el.classList.add('-hide-controls')
        }
    }

    /**
     * Update pagination images
     *
     * @param
     */
    updatePaginationImages(id) {
        // clear current slides
        this.currentPaginationSlides = []

        this.$('pagination-inner')[0].innerHTML = ''

        if(id == null) {
            this.paginationSlides.forEach((item) => {
                this.currentPaginationSlides.push(item.el)
            })
        } else {
            this.paginationSlides.forEach((item) => {
                if(item.ids.includes(id)) {
                    this.currentPaginationSlides.push(item.el)
                }
            })
        }

        //if 1 image or no images found
        if(this.currentPaginationSlides.length == 0) {
            this.paginationSlides.forEach((item) => {
                this.currentPaginationSlides.push(item.el)
            })
        } else if(this.currentPaginationSlides.length == 1) {
            this.currentPaginationSlides = []
        }

        this.currentPaginationSlides.forEach((item) => {
            this.$('pagination-inner')[0].appendChild(item)
        })

        let paginationEls = this.$('pagination-inner')[0].querySelectorAll('[data-carousel-product="item-pagination"]')

        paginationEls.forEach((item) => {
            item.style.backgroundImage = `url("${item.getAttribute('data-background-src')}")`
        })
    }

    // ==========================================================================
    // Controls
    // ==========================================================================
    initControls() {
        if(window.isMobile) return

        this.controlsHasInit = true

        this.controlsInner = this.$('controls-inner')[0];

        this.position = { y: 0 }
        this.mouse = { y: 0 }
        this.lerpedComputedPosition = 0

        this.mouseenterBind = () => { this.animateControls() }

        this.mouseleaveBind = () => {
            cancelAnimationFrame(this.raf)

            this.position = { y: 0 }
            this.mouse = { y: 0 }
            this.lerpedComputedPosition = 0

            gsap.to(this.controlsInner, 0.35, { y:0 })
        }

        this.mousemoveBind = (e) => {
            this.mouse.y = e.clientY
        }

        this.el.addEventListener('mouseenter', this.mouseenterBind.bind(this))
        this.el.addEventListener('mouseleave', this.mouseleaveBind.bind(this))
        this.el.addEventListener('mousemove', this.mousemoveBind.bind(this))
    }

    animateControls() {
        this.controlsBCR    = this.compute(this.controls[0])
        this.parentsBCR     = this.compute(this.controls[0].parentElement)

        this.position.y     = this.controlsBCR.y + (this.controlsBCR.height / 2)

        this.computedPosition = this.position.y - this.mouse.y
        if (this.computedPosition <= 0) this.computedPosition = 0
        if (this.computedPosition + this.controlsBCR.height >= this.parentsBCR.height) this.computedPosition = this.parentsBCR.height - (this.controlsBCR.height)

        this.lerpedComputedPosition = lerp(this.lerpedComputedPosition, this.computedPosition, 0.2)

        gsap.set(this.controlsInner, { y: -this.lerpedComputedPosition })

        this.raf = requestAnimationFrame(() => this.animateControls())
    }

    compute(el) {
        return el.getBoundingClientRect();
    }

    destroy() {
        super.destroy()

        if(this.carousel && this.carousel.destroy) this.carousel.destroy(true,true)

        if(!this.controlsHasInit) return
        this.el.removeEventListener('mouseenter', this.mouseenterBind.bind(this))
        this.el.removeEventListener('mouseleave', this.mouseleaveBind.bind(this))
        this.el.removeEventListener('mousemove', this.mousemoveBind.bind(this))
    }
}
